import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { fetchOrderAttachment, fetchReconMedia, fetchWorkspace, uploadOrderAttachment, workspaceAction } from './api';
import OrderEditor from './OrderEditor';
import type { WorkspacePayload } from './types';

interface Props { caseId: string; token: string; onClose: () => void | Promise<void> }

type WorkspaceTab = 'planning' | 'grouping' | 'recon' | 'orders' | 'publish';
type WorkspaceMode = 'mobile' | 'desktop';

const WORKSPACE_TABS: Array<{ id: WorkspaceTab; label: string; description: string }> = [
  { id: 'planning', label: '規劃', description: 'METT-TC、分工與時間軸' },
  { id: 'grouping', label: '編組', description: '案件名單、組別與車次' },
  { id: 'recon', label: '場勘', description: '場勘項目與回報' },
  { id: 'orders', label: '命令', description: '五段命令草稿' },
  { id: 'publish', label: '發布', description: '版本紀錄與正式發布' },
];

function workspaceTabFromUrl(): WorkspaceTab {
  const value = new URLSearchParams(window.location.search).get('workspaceTab');
  return WORKSPACE_TABS.some((tab) => tab.id === value) ? value as WorkspaceTab : 'planning';
}

function workspaceModeFromUrl(): WorkspaceMode {
  const value = new URLSearchParams(window.location.search).get('workspaceMode');
  if (value === 'mobile' || value === 'desktop') return value;
  return window.matchMedia('(min-width: 900px)').matches ? 'desktop' : 'mobile';
}

function WorkspaceTabIcon({ tab }: { tab: WorkspaceTab }) {
  const paths: Record<WorkspaceTab, ReactNode> = {
    planning: <><path d="M7 3h10v4H7z" /><path d="M5 5v16h14V5M8 11h8M8 15h6" /></>,
    grouping: <><circle cx="9" cy="8" r="3" /><circle cx="17" cy="9" r="2.5" /><path d="M3 20c0-4 2.5-6 6-6s6 2 6 6M15 15c3.5-.5 6 1 6 5" /></>,
    recon: <><path d="M12 21s6-5.2 6-11a6 6 0 1 0-12 0c0 5.8 6 11 6 11Z" /><circle cx="12" cy="10" r="2" /></>,
    orders: <><path d="M6 3h9l3 3v15H6z" /><path d="M14 3v4h4M9 11h6M9 15h6" /></>,
    publish: <><path d="M12 16V4M7 9l5-5 5 5" /><path d="M5 14v7h14v-7" /></>,
  };
  return <svg className="workspace-tab-icon" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{paths[tab]}</svg>;
}

function value(row: Record<string, unknown> | null, key: string): string {
  const item = row?.[key];
  return typeof item === 'string' || typeof item === 'number' ? String(item) : '';
}

export default function Workspace({ caseId, token, onClose }: Props) {
  const [workspace, setWorkspace] = useState<WorkspacePayload | null>(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<{ mediaId: string; url: string } | null>(null);
  const [activeTab, setActiveTab] = useState<WorkspaceTab>(workspaceTabFromUrl);
  const [mode, setMode] = useState<WorkspaceMode>(workspaceModeFromUrl);

  const reload = useCallback(async () => {
    setWorkspace(await fetchWorkspace(caseId, token));
  }, [caseId, token]);

  useEffect(() => { void reload().catch((error) => setMessage(error instanceof Error ? error.message : '載入失敗')); }, [reload]);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview.url); }, [preview]);
  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('workspaceTab', activeTab);
    window.history.replaceState(null, '', url);
  }, [activeTab]);
  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('workspaceMode', mode);
    window.history.replaceState(null, '', url);
    if (mode === 'mobile' && !['grouping', 'recon', 'orders'].includes(activeTab)) setActiveTab('orders');
  }, [mode, activeTab]);
  useEffect(() => () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('workspaceTab');
    window.history.replaceState(null, '', url);
  }, []);

  function selectTab(tab: WorkspaceTab) {
    setActiveTab(tab);
    window.scrollTo({ top: 0, behavior: 'auto' });
    window.requestAnimationFrame(() => document.getElementById(`workspace-panel-title-${tab}`)?.focus());
  }

  async function submit(
    action: string,
    form: HTMLFormElement,
    extra: Record<string, unknown> = {},
    options: { reset?: boolean; successMessage?: string } = {},
  ) {
    setBusy(true);
    setMessage('');
    try {
      const fields = Object.fromEntries(new FormData(form).entries());
      await workspaceAction(token, { action, caseId, ...fields, ...extra });
      if (options.reset) form.reset();
      await reload();
      setMessage(options.successMessage ?? '已儲存。');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '儲存失敗。');
    } finally {
      setBusy(false);
    }
  }

  async function showMedia(mediaId: string) {
    setBusy(true);
    setMessage('');
    try {
      const blob = await fetchReconMedia(token, mediaId);
      if (preview) URL.revokeObjectURL(preview.url);
      setPreview({ mediaId, url: URL.createObjectURL(blob) });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '圖片載入失敗。');
    } finally {
      setBusy(false);
    }
  }

  async function saveOrderSubsection(row: WorkspacePayload['subsections'][number], values: {
    title: string; content: string; tableData: string[][]; referenceType: '' | 'timeline' | 'teams' | 'vehicles';
  }) {
    setBusy(true);
    setMessage('');
    try {
      await workspaceAction(token, {
        action: 'updateSubsection', caseId, subsectionId: row.id, rowVersion: row.row_version, ...values,
      });
      await reload();
      setMessage('這一段已儲存。');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '儲存失敗。');
    } finally { setBusy(false); }
  }

  async function uploadOrderFile(row: WorkspacePayload['subsections'][number], file: File) {
    setBusy(true);
    setMessage('');
    try {
      await uploadOrderAttachment(token, caseId, row.id, file);
      await reload();
      setMessage('附件已上傳。');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '附件上傳失敗。');
    } finally { setBusy(false); }
  }

  async function openOrderFile(attachment: { id: string; fileName: string }) {
    setBusy(true);
    try {
      const blob = await fetchOrderAttachment(token, attachment.id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : '附件載入失敗。');
    } finally { setBusy(false); }
  }

  if (!workspace) return <main className="shell state-page"><p>{message || '正在載入工作區…'}</p></main>;
  const isAdmin = workspace.role === 'CASE_ADMIN';

  return (
    <main className={`shell workspace mode-${mode}`}>
      <div className="workspace-heading">
        <div><p className="section-kicker">計畫工作區</p><h1>案件編輯與發布</h1></div>
        <div className="workspace-heading-actions">
          <div className="mode-switch" role="group" aria-label="工作區模式">
            <button type="button" aria-pressed={mode === 'mobile'} onClick={() => setMode('mobile')}>行動模式</button>
            <button type="button" aria-pressed={mode === 'desktop'} onClick={() => setMode('desktop')}>完整工作區</button>
          </div>
          <button className="secondary-button compact" type="button" onClick={() => void onClose()}>回唯讀頁</button>
        </div>
      </div>
      <p className="workspace-notice" aria-live="polite">{message || `${workspace.role} 權限`}</p>

      <div className="workspace-layout">
        <nav className="workspace-tabs" role="tablist" aria-label="工作區分頁">
          {WORKSPACE_TABS.filter((tab) => mode === 'desktop' || ['grouping', 'recon', 'orders'].includes(tab.id)).map((tab) => <button
            id={`workspace-tab-${tab.id}`}
            key={tab.id}
            className="workspace-tab"
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`workspace-panel-${tab.id}`}
            onClick={() => selectTab(tab.id)}
          >
            <WorkspaceTabIcon tab={tab.id} />
            <span>{tab.label}</span>
          </button>)}
        </nav>

        <div className="workspace-panels">
          <div id="workspace-panel-planning" className="workspace-panel" role="tabpanel" aria-labelledby="workspace-tab-planning" hidden={activeTab !== 'planning'}>
            <header className="workspace-panel-heading"><p className="section-kicker">1 / 5</p><h2 id="workspace-panel-title-planning" tabIndex={-1}>規劃</h2><p>{WORKSPACE_TABS[0].description}</p></header>
            <section className="workspace-card">
              <h3>METT-TC</h3>
              <form className="editor-grid" onSubmit={(event) => { event.preventDefault(); void submit('saveMett', event.currentTarget); }}>
                {[
                  ['mission', '任務'], ['threat', '威脅'], ['terrain', '地形'], ['troops', '兵力'],
                  ['timeInfo', '時間'], ['civil', '民情'],
                ].map(([name, label]) => (
                  <label key={name}>{label}<textarea name={name} defaultValue={value(workspace.mett, name === 'timeInfo' ? 'time_info' : name)} rows={3} /></label>
                ))}
                <button className="primary-button" disabled={busy}>建立新版本</button>
              </form>
            </section>

            <section className="workspace-card">
              <h3>計畫分工</h3>
              <ul className="plain-list">{workspace.tasks.map((task) => <li key={value(task, 'id')}><strong>{value(task, 'title')}</strong><span>{value(task, 'assignedName') || '未指派'} · {value(task, 'status')}</span></li>)}</ul>
              {isAdmin && <form className="inline-form" onSubmit={(event) => { event.preventDefault(); void submit('addTask', event.currentTarget, {}, { reset: true }); }}>
                <label>工作<input name="title" required maxLength={120} /></label>
                <label>負責人<select name="assignedUserId"><option value="">未指派</option>{workspace.members.map((member) => <option key={value(member, 'userId')} value={value(member, 'userId')}>{value(member, 'name')}</option>)}</select></label>
                <label>期限<input name="dueAt" type="datetime-local" /></label>
                <button className="primary-button" disabled={busy}>新增分工</button>
              </form>}
            </section>

            <section className="workspace-card">
              <h3>時間軸</h3>
              {(['planning', 'operation'] as const).map((kind) => <div key={kind}>
                <h4>{kind === 'planning' ? '計畫時間' : '勤務時間'}</h4>
                <ul className="plain-list">{workspace.timeline.filter((event) => value(event, 'kind') === kind).map((event) => <li key={value(event, 'id')}><strong>{value(event, 'title')}</strong><span>{value(event, 'at')}</span></li>)}</ul>
              </div>)}
              <form className="inline-form" onSubmit={(event) => { event.preventDefault(); void submit('addTimeline', event.currentTarget, {}, { reset: true }); }}>
                <label>類型<select name="kind"><option value="planning">計畫</option><option value="operation">勤務</option></select></label>
                <label>時間<input name="at" type="datetime-local" required /></label>
                <label>標題<input name="title" required /></label>
                <button className="primary-button" disabled={busy}>新增時間</button>
              </form>
            </section>
          </div>

          <div id="workspace-panel-grouping" className="workspace-panel" role="tabpanel" aria-labelledby="workspace-tab-grouping" hidden={activeTab !== 'grouping'}>
            <header className="workspace-panel-heading"><p className="section-kicker">2 / 5</p><h2 id="workspace-panel-title-grouping" tabIndex={-1}>編組</h2><p>{WORKSPACE_TABS[1].description}</p></header>
            {isAdmin && <section className="workspace-card">
              <h3>案件名單</h3>
              <ul className="plain-list">{workspace.members.map((member) => <li key={value(member, 'id')}><strong>{value(member, 'name')}</strong><span>{value(member, 'role')} · {Number(member.claimed) ? '已認領' : '待認領'}</span></li>)}</ul>
              <form className="inline-form" onSubmit={(event) => { event.preventDefault(); void submit('addMember', event.currentTarget, {}, { reset: true }); }}>
                <label>姓名<input name="name" required maxLength={60} /></label>
                <label>單位<input name="unit" /></label>
                <label>職務<input name="position" /></label>
                <label>角色<select name="role"><option value="VIEWER">VIEWER</option><option value="EDITOR">EDITOR</option><option value="CASE_ADMIN">CASE_ADMIN</option></select></label>
                <button className="primary-button" disabled={busy}>新增待認領人員</button>
              </form>
            </section>}

            <section className="workspace-card">
              <h3>執行編組與車次</h3>
              <p>這裡是發布前草稿；重新發布後才會更新同仁的「我的編組」。</p>
              <div className="editor-grid phase2-grid">
                <div>
                  <h4>執行組別</h4>
                  <ul className="plain-list">{workspace.teams.map((team) => {
                    const members = workspace.members.filter((member) => value(member, 'teamId') === value(team, 'id'));
                    return <li key={value(team, 'id')}><strong>{value(team, 'name')}</strong><span>{members.map((member) => value(member, 'name')).join('、') || '尚無成員'}</span></li>;
                  })}</ul>
                  {isAdmin && <form className="inline-form single-column-form" onSubmit={(event) => { event.preventDefault(); void submit('addTeam', event.currentTarget, {}, { reset: true }); }}>
                    <label>組別名稱<input name="name" required maxLength={80} /></label>
                    <label>說明<input name="description" maxLength={200} /></label>
                    <button className="primary-button" disabled={busy}>新增組別</button>
                  </form>}
                </div>
                <div>
                  <h4>車次</h4>
                  <ul className="plain-list">{workspace.vehicles.map((vehicle) => {
                    const members = workspace.members.filter((member) => value(member, 'vehicleId') === value(vehicle, 'id'));
                    return <li key={value(vehicle, 'id')}><strong>{value(vehicle, 'code')}</strong><span>{members.length} / {value(vehicle, 'seat_count')} 人</span></li>;
                  })}</ul>
                  {isAdmin && <form className="inline-form single-column-form" onSubmit={(event) => { event.preventDefault(); void submit('addVehicle', event.currentTarget, {}, { reset: true }); }}>
                    <label>車次名稱<input name="code" required maxLength={40} /></label>
                    <label>座位數<input name="seatCount" type="number" inputMode="numeric" min={1} max={60} required /></label>
                    <label>備註<input name="notes" maxLength={200} /></label>
                    <button className="primary-button" disabled={busy}>新增車次</button>
                  </form>}
                </div>
              </div>
              {isAdmin && <div className="assignment-list">
                <h4>人員分派</h4>
                {workspace.members.map((member) => <form key={`${value(member, 'id')}:${value(member, 'teamId')}:${value(member, 'vehicleId')}:${value(member, 'vehicleSeat')}`} className="assignment-row" onSubmit={(event) => {
                  event.preventDefault();
                  void submit('assignMember', event.currentTarget, { memberId: value(member, 'id') }, {
                    successMessage: '編組草稿已儲存；重新發布後會更新唯讀頁。',
                  });
                }}>
                  <strong>{value(member, 'name')}</strong>
                  <label>組別<select name="teamId" defaultValue={value(member, 'teamId')}><option value="">未分組</option>{workspace.teams.map((team) => <option key={value(team, 'id')} value={value(team, 'id')}>{value(team, 'name')}</option>)}</select></label>
                  <label>車次<select name="vehicleId" defaultValue={value(member, 'vehicleId')}><option value="">未搭車</option>{workspace.vehicles.map((vehicle) => <option key={value(vehicle, 'id')} value={value(vehicle, 'id')}>{value(vehicle, 'code')}</option>)}</select></label>
                  <label>座位<input name="vehicleSeat" type="number" inputMode="numeric" min={1} max={60} defaultValue={value(member, 'vehicleSeat')} /></label>
                  <button className="secondary-button compact" disabled={busy}>儲存分派</button>
                </form>)}
              </div>}
            </section>
          </div>

          <div id="workspace-panel-recon" className="workspace-panel" role="tabpanel" aria-labelledby="workspace-tab-recon" hidden={activeTab !== 'recon'}>
            <header className="workspace-panel-heading"><p className="section-kicker">3 / 5</p><h2 id="workspace-panel-title-recon" tabIndex={-1}>場勘</h2><p>{WORKSPACE_TABS[2].description}</p></header>
            <section className="workspace-card">
              <h3>場勘與回報</h3>
              <p>同仁在案件頁用同一張表單送出文字與照片；文字離線草稿會留在裝置上，圖片送出時需有網路。</p>
              <ul className="plain-list">{workspace.reconItems.map((item) => <li key={value(item, 'id')}><strong>{value(item, 'title')}</strong><span>{value(item, 'assignedName') || '未指派'} · {value(item, 'status')}</span></li>)}</ul>
              <form className="inline-form" onSubmit={(event) => { event.preventDefault(); void submit('addReconItem', event.currentTarget, {}, { reset: true }); }}>
                <label>場勘項目<input name="title" required maxLength={120} /></label>
                <label>負責人<select name="assignedUserId"><option value="">未指派</option>{workspace.members.map((member) => <option key={value(member, 'userId')} value={value(member, 'userId')}>{value(member, 'name')}</option>)}</select></label>
                <label>說明<input name="description" maxLength={500} /></label>
                <button className="primary-button" disabled={busy}>新增場勘項目</button>
              </form>
              {workspace.reconReports.length > 0 && <div className="report-list">
                <h4>已送出回報</h4>
                {workspace.reconReports.map((report) => <article key={value(report, 'id')} className="latest-report">
                  <strong>{value(report, 'itemTitle')} · {value(report, 'reporterName')}</strong>
                  <p>{value(report, 'content')}</p>
                  <small>{value(report, 'observed_at')}</small>
                  <div className="media-list">{workspace.reconMedia.filter((media) => value(media, 'reportId') === value(report, 'id')).map((media) => <button className="text-button" type="button" key={value(media, 'id')} disabled={busy} onClick={() => void showMedia(value(media, 'id'))}>查看 {value(media, 'fileName')}</button>)}</div>
                  {preview && workspace.reconMedia.some((media) => value(media, 'reportId') === value(report, 'id') && value(media, 'id') === preview.mediaId)
                    && <img className="recon-preview" src={preview.url} alt={`${value(report, 'itemTitle')} 場勘回報圖片`} />}
                </article>)}
              </div>}
            </section>
          </div>

          <div id="workspace-panel-orders" className="workspace-panel" role="tabpanel" aria-labelledby="workspace-tab-orders" hidden={activeTab !== 'orders'}>
            <header className="workspace-panel-heading"><p className="section-kicker">4 / 5</p><h2 id="workspace-panel-title-orders" tabIndex={-1}>命令</h2><p>{WORKSPACE_TABS[3].description}</p></header>
            <section className="workspace-card">
              <h3>五段命令草稿</h3>
              <p>{mode === 'mobile' ? '一次只快修一個小標；完整編排請切換至完整工作區。' : '左側選段落、中間編輯、右側即時確認發布後的閱讀效果。'}</p>
              <OrderEditor mode={mode} workspace={workspace} busy={busy} onSave={saveOrderSubsection} onUpload={uploadOrderFile} onOpenAttachment={openOrderFile} />
            </section>
          </div>

          <div id="workspace-panel-publish" className="workspace-panel" role="tabpanel" aria-labelledby="workspace-tab-publish" hidden={activeTab !== 'publish'}>
            <header className="workspace-panel-heading"><p className="section-kicker">5 / 5</p><h2 id="workspace-panel-title-publish" tabIndex={-1}>發布</h2><p>{WORKSPACE_TABS[4].description}</p></header>
            <section className="workspace-card publish-card">
              <h3>版本紀錄</h3>
              <ul className="plain-list">{workspace.versions.map((version) => <li key={value(version, 'id')}><strong>{value(version, 'version_label')}</strong><span>{value(version, 'published_at')}</span></li>)}</ul>
              {workspace.versions.length === 0 && <p className="empty-state">尚未正式發布任何版本。</p>}
            </section>
            <section className="workspace-card publish-card">
              <h3>正式發布</h3>
              <p>發布會凍結五段命令、執行編組、車次與勤務時間。每次重新發布都會清除上一版確認，確認人數從 0 重新計算。</p>
              {isAdmin ? <form className="inline-form" onSubmit={(event: FormEvent<HTMLFormElement>) => {
                event.preventDefault();
                void submit('publish', event.currentTarget, {}, {
                  reset: true,
                  successMessage: '已正式發布；確認人數已歸零。回唯讀頁即可查看最新版。',
                });
              }}>
                <label>版本<input name="versionLabel" placeholder="v1.1" pattern="v[0-9]+\.[0-9]+" required /></label>
                <button className="primary-button" disabled={busy}>正式發布</button>
              </form> : <p className="permission-note">只有 CASE_ADMIN 可以正式發布；你仍可在這裡查看版本紀錄。</p>}
            </section>
          </div>
        </div>
      </div>
    </main>
  );
}
